import PropTypes from "prop-types";
import React from "react";

import { classNames } from "@swa-ui/string";

import { Text } from "../Text";
import styles from "./GroupedRow.module.scss";

/**
 * GroupedRow renders a div with label/value pair. The value (children) is rendered below the content.
 */
export const GroupedRow = (props) => {
  const { children, className, light = false, size = "large", styleType = "normal", title } = props;

  return (
    <div className={classNames(styles.groupedRow, className)}>
      <Text {...getTitleProps()}>{title}</Text>
      <Text {...getValueProps()}>{children}</Text>
    </div>
  );

  function getTitleProps() {
    return {
      bold: true,
      color: getTitleColor(),
      size,
      textType: "label",
    };
  }

  function getValueProps() {
    return {
      color: getValueColor(),
      size: size === "large" ? "xlarge" : "small",
      textType: "body",
    };
  }

  function getTitleColor() {
    const isSubtleTitle = styleType === "subtle";
    const isNormalTitle = styleType === "normal" && !light;

    return isNormalTitle
      ? null
      : light
      ? isSubtleTitle
        ? "var(--cmp-core-color-grouped-row-light-subtle)"
        : "var(--cmp-core-color-grouped-row-light)"
      : isSubtleTitle
      ? "var(--cmp-core-color-grouped-row-subtle)"
      : "";
  }

  function getValueColor() {
    const isSubtleValue = styleType.includes("subtle");
    const isNormalValue = styleType === "normal" && !light;

    return isNormalValue
      ? null
      : light
      ? isSubtleValue
        ? "var(--cmp-core-color-grouped-row-light-subtle)"
        : "var(--cmp-core-color-grouped-row-light)"
      : isSubtleValue
      ? "var(--cmp-core-color-grouped-row-subtle)"
      : "";
  }
};

GroupedRow.displayName = "GroupedRow";

export const GroupedRowPropTypes = {
  /**
   * Content that will be rendered under title. For both the child content and the title, a
   * font size will be set and applied when not overridden by the child/title content.
   */
  children: PropTypes.node.isRequired,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** Provide light styling to accommodate rendering on a dark background. */
  light: PropTypes.bool,

  /** Size to be applied to both the title and the child content. */
  size: PropTypes.oneOf(["large", "small"]),

  /** If true, will render child content text using a faint shade, typically gray. */
  styleType: PropTypes.oneOf(["normal", "subtle", "subtleValue"]),

  /**
   * Title to "label" child content, which is rendered below this title. Title will receive default
   * styling, including a bold weight, unless overridden by the node content given.
   */
  title: PropTypes.node.isRequired,
};

GroupedRow.propTypes = GroupedRowPropTypes;
